import {
  Component,
  OnInit,
  OnChanges,
  Input,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  QaiDatepickerMaterialComponent,
  QaiFloatingSelectOption,
  QaiFloatingSelectComponent,
  QaiInnerWrapComponent,
  QaiLoadingComponent,
  QaiLinkButtonComponent,
  type HeaderConfig,
  TableRow,
  QaiTableComponent,
} from '@qai/ui';
import { LocaleService } from 'src/app/services/locale.service';
import { TranslationService } from 'src/app/services/translation.service';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { getAccountNameByUuid } from 'src/app/utils/local-storage.util';
import { IPersonalizedPracticeSessionReport } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session-report';
import { PersonalizedPracticeSessionsChartComponent } from "./personalized-practice-sessions-chart/personalized-practice-sessions-chart.component";

@Component({
  selector: 'app-reporting-personalized-practice-sessions',
  imports: [
    CommonModule,
    TranslocoModule,
    QaiDatepickerMaterialComponent,
    QaiFloatingSelectComponent,
    QaiInnerWrapComponent,
    QaiLoadingComponent,
    QaiLinkButtonComponent,
    QaiTableComponent,
    PersonalizedPracticeSessionsChartComponent
  ],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  templateUrl: './personalized-practice-sessions.component.html',
  styleUrl: './personalized-practice-sessions.component.scss',
})
export class PersonalizedPracticeSessionsComponent
  implements OnInit, OnChanges
{
  @Input() moduleUuid: string = '';
  @Input() teamUuids: string[] = [];
  @Input() productName: string = '';

  isLoading: boolean = true;
  isTranslationLoaded: boolean = false;
  // Data
  sessions: IPersonalizedPracticeSessionReport[] = [];
  filteredSessions: IPersonalizedPracticeSessionReport[] = [];
  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];
  filteredTableRows: TableRow[] = [];
  initialSortColumn: string = '';
  initialSortDirection: 'desc' = 'desc';

  // Date filter
  selectedDate: Date | null = null;
  currentLocale: string = '';

  // Learner
  learnerOptions: QaiFloatingSelectOption[] = [];
  learnerSelectedValue: string = '';
  learner: string[] = [];

  // Conversation type
  conversationTypeOptions: QaiFloatingSelectOption[] = [];
  conversationTypeSelectedValue: string = '';
  conversationType: string[] = [];

  // Difficulty
  difficultyTypeOptions: QaiFloatingSelectOption[] = [];
  difficultyTypeSelectedValue: string = '';
  difficultyType: string[] = [];

  // Mood (temperament)
  moodOptions: QaiFloatingSelectOption[] = [];
  moodSelectedValue: string = '';
  mood: string[] = [];

  constructor(
    private localeService: LocaleService,
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.currentLocale = this.localeService.getCurrentLanguage();

    this.translationService
      .loadScopedTranslation('personalizedPractice')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeHeaders();
        this.initialSortColumn = this.headers[2].name;
        this.loadSessions();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload sessions when moduleUuid or teamUuids change
    if (
      (changes['moduleUuid'] && !changes['moduleUuid'].firstChange) ||
      (changes['teamUuids'] && !changes['teamUuids'].firstChange)
    ) {
      if (this.moduleUuid) {
        this.loadSessions();
      }
    }
  }

  private initializeHeaders(): void {
    this.headers = [
      {
        name: this.translationService.translate(
          'LEARNER',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'SESSION_DATE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'DURATION',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'CONVERSATION_TYPE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'DIFFICULTY',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'TEMPERAMENT',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'OVERALL_SCORE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
    ];
  }

  private loadSessions(): void {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    if (!accountUuid || !this.moduleUuid) {
      console.warn('Missing accountUuid or moduleUuid for loading sessions');
      this.isLoading = false;
      return;
    }

    const teamUuids = this.teamUuids.includes('all') ? [] : this.teamUuids;

    this.isLoading = true;
    this.reportingDashboardService
      .getPersonalizedPracticeCallStats({
        accountUuid,
        moduleUuid: this.moduleUuid,
        teamUuids: teamUuids,
      })
      .subscribe({
        next: (sessions: IPersonalizedPracticeSessionReport[]) => {
          this.sessions = sessions;
          if (this.sessions && this.sessions.length > 0) {
            this.tableData = this.transformDataToTableRows(this.sessions);
            this.filteredTableRows = this.tableData;
            this.filteredSessions = this.sessions;
            this.getUniqueSelectValues();
          } else {
            this.tableData = [];
            this.filteredTableRows = [];
            this.filteredSessions = [];
          }
        },
        error: (error) => {
          console.error('Error loading personalized practice sessions:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  private transformDataToTableRows(
    apiData: IPersonalizedPracticeSessionReport[]
  ): TableRow[] {
    return apiData.map((session) => this.createSessionTableRow(session));
  }

  private formatDuration(durationInSeconds: string | number): string {
    const seconds =
      typeof durationInSeconds === 'string'
        ? parseInt(durationInSeconds, 10)
        : durationInSeconds;

    if (isNaN(seconds)) {
      return String(durationInSeconds);
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  private createSessionTableRow(
    session: IPersonalizedPracticeSessionReport
  ): TableRow {
    const row = {
      id: session.practiceCallUuid,
      callUuid: session.callUuid,
      [this.headers[0].name]: {
        type: 'text',
        value: session.userName || '-',
      },
      [this.headers[1].name]: {
        type: 'text',
        value: this.localeService.formatDateLong(session.scoredAt),
      },
      [this.headers[2].name]: {
        type: 'text',
        value: this.formatDuration(session.durationInSeconds),
      },
      [this.headers[3].name]: {
        type: 'text',
        value: session.callName ? this.capitalizeWords(session.callName) : '-',
      },
      [this.headers[4].name]: {
        type: 'text',
        value: session.difficulty
          ? this.capitalizeWords(session.difficulty)
          : '-',
      },
      [this.headers[5].name]: {
        type: 'text',
        value: session.temperament
          ? this.capitalizeWords(session.temperament)
          : '-',
      },
      [this.headers[6].name]: {
        type: 'text',
        value: session.scoreOverall?.toString() ?? '-',
      },
    } as const;

    return { ...row } as unknown as TableRow;
  }

  private getUniqueSelectValues(): void {
    // Extract unique learners
    this.learner = [
      ...new Set(
        this.sessions
          .map((session) => session.userName)
          .filter((userName): userName is string => userName != null)
      ),
    ].sort();
    this.learnerOptions = [
      ...this.learner.map((name) => ({
        label: name,
        value: name,
      })),
    ];

    // Extract unique conversation types
    this.conversationType = [
      ...new Set(
        this.sessions
          .map((session) => session.callName)
          .filter((callName): callName is string => callName != null)
      ),
    ].sort();
    this.conversationTypeOptions = [
      ...this.conversationType.map((type) => ({
        label: this.capitalizeWords(type),
        value: type,
      })),
    ];

    // Extract unique difficulty types
    this.difficultyType = [
      ...new Set(
        this.sessions
          .map((session) => session.difficulty)
          .filter((difficulty): difficulty is string => difficulty != null)
      ),
    ].sort();
    this.difficultyTypeOptions = [
      ...this.difficultyType.map((type) => ({
        label: this.capitalizeWords(type),
        value: type,
      })),
    ];

    // Extract unique mood/temperament types
    this.mood = [
      ...new Set(
        this.sessions
          .map((session) => session.temperament)
          .filter((temperament): temperament is string => temperament != null)
      ),
    ].sort();
    this.moodOptions = [
      ...this.mood.map((type) => ({
        label: this.capitalizeWords(type),
        value: type,
      })),
    ];
  }

  private capitalizeWords(str: string): string {
    return str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onDateChange(value: Date | null): void {
    this.selectedDate = value;
    this.applyFilters('date');
  }

  onConversationTypeChange(value: string | null): void {
    this.conversationTypeSelectedValue = value || '';
    this.applyFilters('conversation_type');
  }

  onDifficultyChange(value: string | null): void {
    this.difficultyTypeSelectedValue = value || '';
    this.applyFilters('difficulty');
  }

  onMoodChange(value: string | null): void {
    this.moodSelectedValue = value || '';
    this.applyFilters('mood');
  }

  onLearnerChange(value: string | null): void {
    this.learnerSelectedValue = value || '';
    this.applyFilters('learner');
  }

  onClearFilters(event: Event): void {
    event.preventDefault();
    this.selectedDate = null;
    this.conversationTypeSelectedValue = '';
    this.difficultyTypeSelectedValue = '';
    this.moodSelectedValue = '';
    this.learnerSelectedValue = '';
    this.applyFilters('clear');
  }

  private trackFilterApplied(
    filterType: string,
    filterValue: string | null
  ): void {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    const accountName = accountUuid ? getAccountNameByUuid(accountUuid) : '';

    // Count how many filters are currently active
    let activeFilterCount = 0;
    if (this.selectedDate) activeFilterCount++;
    if (this.learnerSelectedValue) activeFilterCount++;
    if (this.conversationTypeSelectedValue) activeFilterCount++;
    if (this.difficultyTypeSelectedValue) activeFilterCount++;
    if (this.moodSelectedValue) activeFilterCount++;

    // Format filter value for date
    let formattedFilterValue = filterValue || '';
    if (filterType === 'date' && this.selectedDate) {
      formattedFilterValue = this.selectedDate.toISOString().split('T')[0];
    }

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.PERSONALIZED_PRACTICE_REPORTING_PAGE_FILTER_APPLIED,
      {
        product_name: this.productName,
        account_name: accountName,
        filter_type: filterType,
        filter_value: formattedFilterValue,
        filter_count: activeFilterCount,
      }
    );
  }

  private applyFilters(filterType?: string): void {
    let filtered = [...this.tableData];

    // Filter by date
    if (this.selectedDate) {
      filtered = filtered.filter((row) => {
        const session = this.sessions.find(
          (s) => s.practiceCallUuid === row.id
        );
        if (!session) return false;

        const sessionDate = new Date(session.scoredAt);

        // Compare only the date part (ignore time)
        return (
          sessionDate.getUTCFullYear() === this.selectedDate!.getFullYear() &&
          sessionDate.getUTCMonth() === this.selectedDate!.getMonth() &&
          sessionDate.getUTCDate() === this.selectedDate!.getDate()
        );
      });
    }

    // Filter by learner
    if (this.learnerSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.sessions.find(
          (s) => s.practiceCallUuid === row.id
        );
        return session?.userName === this.learnerSelectedValue;
      });
    }

    // Filter by conversation type
    if (this.conversationTypeSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.sessions.find(
          (s) => s.practiceCallUuid === row.id
        );
        return session?.callName === this.conversationTypeSelectedValue;
      });
    }

    // Filter by difficulty
    if (this.difficultyTypeSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.sessions.find(
          (s) => s.practiceCallUuid === row.id
        );
        return session?.difficulty === this.difficultyTypeSelectedValue;
      });
    }

    // Filter by mood/temperament
    if (this.moodSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.sessions.find(
          (s) => s.practiceCallUuid === row.id
        );
        return session?.temperament === this.moodSelectedValue;
      });
    }

    this.filteredTableRows = filtered;

    // Derive filtered session data from filtered table rows
    this.filteredSessions = this.filteredTableRows
      .map((row) => this.sessions.find((s) => s.practiceCallUuid === row.id))
      .filter(
        (session): session is IPersonalizedPracticeSessionReport =>
          session !== undefined
      );

    // Track filter applied event if filterType is provided
    if (filterType && filterType !== 'clear') {
      let filterValue: string | null = null;
      switch (filterType) {
        case 'date':
          filterValue = this.selectedDate
            ? this.selectedDate.toISOString()
            : null;
          break;
        case 'learner':
          filterValue = this.learnerSelectedValue;
          break;
        case 'conversation_type':
          filterValue = this.conversationTypeSelectedValue;
          break;
        case 'difficulty':
          filterValue = this.difficultyTypeSelectedValue;
          break;
        case 'mood':
          filterValue = this.moodSelectedValue;
          break;
      }
      this.trackFilterApplied(filterType, filterValue);
    }
  }

  protected get areFiltersApplied(): boolean {
    return !!(
      this.selectedDate ||
      this.learnerSelectedValue ||
      this.conversationTypeSelectedValue ||
      this.difficultyTypeSelectedValue ||
      this.moodSelectedValue
    );
  }
}
